import { z } from "zod";
import { discordSnowflakeSchema, utcTimestampSchema, } from "./guild-settings.js";
import { boundedNfcTextSchema, sanitizedJsonObjectSchema, uuidSchema, } from "./platform-common.js";
export const ENTITLEMENT_CONTRACT_VERSION = 1;
export const entitlementEffectiveStateSchema = z.enum([
    "trial",
    "subscribed",
    "unsubscribed",
    "complimentary",
]);
export const entitlementProviderSchema = z.literal("stripe");
export const entitlementMutationSourceSchema = z.enum([
    "linked_leader",
    "superadmin",
    "stripe",
    "system",
]);
export const entitlementMutationActionSchema = z.enum([
    "activate_trial",
    "set_manual_subscription",
    "clear_manual_subscription",
    "grant_complimentary",
    "revoke_complimentary",
    "project_provider_subscription",
]);
export const entitlementActorSourceSchema = z.enum([
    "web",
    "admin",
    "system",
    "stripe",
]);
export const guildEntitlementProjectionSchema = z
    .object({
    contractVersion: z.literal(ENTITLEMENT_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    effectiveState: entitlementEffectiveStateSchema,
    revision: z.int().positive(),
    trialStartedAt: utcTimestampSchema.nullable(),
    trialEndsAt: utcTimestampSchema.nullable(),
    manualSubscriptionActive: z.boolean(),
    providerSubscriptionActive: z.boolean(),
    complimentaryActive: z.boolean(),
    provider: entitlementProviderSchema.nullable(),
    updatedAt: utcTimestampSchema,
})
    .strict()
    .superRefine((value, context) => {
    if ((value.trialStartedAt === null) !== (value.trialEndsAt === null)) {
        context.addIssue({
            code: "custom",
            message: "Trial start and end timestamps must be present together",
            path: ["trialEndsAt"],
        });
    }
    if (value.trialStartedAt !== null &&
        Date.parse(value.trialEndsAt) - Date.parse(value.trialStartedAt) !==
            7 * 24 * 60 * 60 * 1_000) {
        context.addIssue({
            code: "custom",
            message: "Trial duration must be exactly seven days",
            path: ["trialEndsAt"],
        });
    }
    if (value.providerSubscriptionActive !== (value.provider !== null)) {
        context.addIssue({
            code: "custom",
            message: "Provider presence must match provider subscription activity",
            path: ["provider"],
        });
    }
    const hasTrial = value.trialStartedAt !== null;
    const validEffectiveStates = value.complimentaryActive
        ? ["complimentary"]
        : value.manualSubscriptionActive || value.providerSubscriptionActive
            ? ["subscribed"]
            : hasTrial
                ? ["trial", "unsubscribed"]
                : ["unsubscribed"];
    if (!validEffectiveStates.includes(value.effectiveState)) {
        context.addIssue({
            code: "custom",
            message: "Effective state contradicts the entitlement projection",
            path: ["effectiveState"],
        });
    }
});
const entitlementMutationBase = {
    contractVersion: z.literal(ENTITLEMENT_CONTRACT_VERSION),
    expectedRevision: z.int().positive(),
};
const adminReasonSchema = boundedNfcTextSchema(500, 2_048);
export const guildEntitlementMutationSchema = z.discriminatedUnion("action", [
    z
        .object({
        ...entitlementMutationBase,
        action: z.literal("activate_trial"),
        source: z.literal("linked_leader"),
    })
        .strict(),
    z
        .object({
        ...entitlementMutationBase,
        action: z.literal("set_manual_subscription"),
        source: z.literal("superadmin"),
        reason: adminReasonSchema,
    })
        .strict(),
    z
        .object({
        ...entitlementMutationBase,
        action: z.literal("clear_manual_subscription"),
        source: z.literal("superadmin"),
        reason: adminReasonSchema,
    })
        .strict(),
    z
        .object({
        ...entitlementMutationBase,
        action: z.literal("grant_complimentary"),
        source: z.literal("superadmin"),
        reason: adminReasonSchema,
    })
        .strict(),
    z
        .object({
        ...entitlementMutationBase,
        action: z.literal("revoke_complimentary"),
        source: z.literal("superadmin"),
        reason: adminReasonSchema,
    })
        .strict(),
    z
        .object({
        ...entitlementMutationBase,
        action: z.literal("project_provider_subscription"),
        source: z.enum(["stripe", "system"]),
        provider: entitlementProviderSchema,
        active: z.boolean(),
        reason: adminReasonSchema.optional(),
    })
        .strict(),
]);
export const guildProviderSubscriptionProjectionSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    contractVersion: z.literal(ENTITLEMENT_CONTRACT_VERSION),
    action: z.literal("project_provider_subscription"),
    source: z.enum(["stripe", "system"]),
    expectedRevision: z.int().positive(),
    provider: entitlementProviderSchema,
    active: z.boolean(),
    reason: adminReasonSchema.optional(),
})
    .strict();
export const entitlementAuditDisplaySchema = z
    .object({
    eventId: uuidSchema,
    guildId: discordSnowflakeSchema,
    actorUserId: discordSnowflakeSchema.nullable(),
    actorSource: entitlementActorSourceSchema,
    action: entitlementMutationActionSchema,
    source: entitlementMutationSourceSchema,
    reason: adminReasonSchema.nullable(),
    previousState: sanitizedJsonObjectSchema(8_192),
    resultingState: sanitizedJsonObjectSchema(8_192),
    createdAt: utcTimestampSchema,
})
    .strict();
//# sourceMappingURL=guild-entitlements.js.map